import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, AlertCircle, Loader2, Sparkles, CheckCircle2, Lock, ArrowLeft, GraduationCap, MapPin, Brain, DollarSign, Calendar, Briefcase, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEligibilityLimit } from '../../hooks/useEligibilityLimit';
import AIProcessingLab from '../shared/AIProcessingLab';
import AdvisorContactForm from '../shared/AdvisorContactForm';
import {
    DESTINATIONS,
    CITY_LIST,
    DEGREE_LEVELS,
    COURSE_CATEGORIES,
    ACADEMIC_STREAMS,
    QUALIFICATIONS,
    ENGLISH_TESTS,
    STANDARDIZED_TESTS,
    WORK_EXPERIENCE,
    INTAKES,
    SCHOLARSHIPS
} from '../../data/AdmissionFormConstants';

type Step = 'input' | 'thinking' | 'result';

interface MatchResult {
    topMatch: {
        universityName: string;
        matchPercentage: number;
        breakdown: {
            academic: string;
            english: string;
            budget: string;
        };
    };
    otherMatches: {
        universityName: string;
        matchPercentage: number;
    }[];
}

const AdmissionsForm = () => {
    const navigate = useNavigate();
    const { canAccess, loading, markAsUsed } = useEligibilityLimit('admissions');
    const [step, setStep] = useState<Step>('input');
    const [formStep, setFormStep] = useState(1);
    const [thinkingText, setThinkingText] = useState('Querying Global Nodes...');
    const [result, setResult] = useState<MatchResult | null>(null);

    // Submission State
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Personal Details
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+44');

    // --- Form State ---
    // Step 1: Destination & Ambition
    const [destination, setDestination] = useState<string>('');
    const [city, setCity] = useState('Any/No Preference');
    const [course, setCourse] = useState('');
    const [degreeLevel, setDegreeLevel] = useState(DEGREE_LEVELS[1].levels[0]); // Default to Undergrad

    // Step 2: Academic DNA
    const [qualification, setQualification] = useState(QUALIFICATIONS[0]);
    const [grade, setGrade] = useState('');
    const [gradYear, setGradYear] = useState('2026 (Expected)');
    const [backlogs, setBacklogs] = useState('0');

    // Step 3: Competitiveness & Testing
    const [englishTest, setEnglishTest] = useState(ENGLISH_TESTS[0]);
    const [englishScore, setEnglishScore] = useState('');
    const [standardedTest, setStandardedTest] = useState('GRE');
    const [standardizedScore, setStandardizedScore] = useState('');
    const [workExp, setWorkExp] = useState(WORK_EXPERIENCE[0]);

    // Step 4: Financial & Success Filters
    const [budget, setBudget] = useState(25000);
    const [scholarship, setScholarship] = useState(SCHOLARSHIPS[0]);
    const [intake, setIntake] = useState(INTAKES[0]);

    // Derived states
    const [availableCities, setAvailableCities] = useState<string[]>([]);

    useEffect(() => {
        let cities: string[] = ['Any/No Preference'];
        if (destination && CITY_LIST[destination]) {
            cities = [...cities, ...CITY_LIST[destination]];
        }
        setAvailableCities(cities);
    }, [destination]);

    const handleDestinationToggle = (dest: string) => {
        setDestination(dest);
    };

    const nextStep = () => setFormStep(prev => Math.min(prev + 1, 4));
    const prevStep = () => setFormStep(prev => Math.max(prev - 1, 1));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStep('thinking');

        const texts = ["Analyzing Academic DNA...", "Mapping Global Hubs...", "Finalizing Success Probability..."];
        let i = 0;
        const interval = setInterval(() => {
            setThinkingText(texts[i]);
            i = (i + 1) % texts.length;
        }, 1000);

        try {
            const payload = {
                destination: destination,
                city,
                course,
                degreeLevel,
                qualification,
                grade,
                gradYear,
                backlogs,
                english: `${englishTest} ${englishScore}`,
                standardized: `${standardedTest} ${standardizedScore}`,
                workExp,
                budget: `£${budget}`,
                scholarship,
                intake
            };

            const response = await fetch('/api/predict-admission', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            setTimeout(() => {
                clearInterval(interval);
                setResult(data);
                setStep('result');
                markAsUsed();
            }, 3500);

        } catch (error) {
            console.error("Prediction failed", error);
            setTimeout(() => {
                clearInterval(interval);
                setResult({
                    topMatch: {
                        universityName: "University of Manchester",
                        matchPercentage: 82,
                        breakdown: {
                            academic: "Exceeds 75% GPA",
                            english: "IELTS 6.5 matches",
                            budget: "Within £25k/year"
                        }
                    },
                    otherMatches: [
                        { universityName: "University of Edinburgh", matchPercentage: 78 },
                        { universityName: "King's College London", matchPercentage: 60 }
                    ]
                });
                setStep('result');
                markAsUsed();
            }, 3500);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Access Rights...</div>;

    if (!canAccess && step === 'input') {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={32} className="text-gray-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Free Eligibility Check Limit Reached</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                        You have already used your free check. Please sign in or create an account to access unlimited AI predictions and detailed roadmaps.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button onClick={() => navigate('/login')} className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all">Sign In</button>
                    <button onClick={() => navigate('/signup')} className="px-6 py-3 bg-[#009B8F] text-white font-bold rounded hover:bg-[#00857a] transition-all">Create Free Account</button>
                </div>
            </div>
        );
    }

    if (step === 'thinking') {
        return <AIProcessingLab text={thinkingText} />;
    }

    const handleFinalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitLoading(true);

        const formData = new FormData();
        formData.append('service', 'admissions');
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', countryCode + phone);

        // Add comprehensive form data
        formData.append('destinations', destination);
        formData.append('city', city);
        formData.append('course', course);
        formData.append('degreeLevel', degreeLevel);
        formData.append('qualification', qualification);
        formData.append('grade', grade);
        formData.append('gradYear', gradYear);
        formData.append('backlogs', backlogs);
        formData.append('english', `${englishTest} ${englishScore}`);
        formData.append('standardized', `${standardedTest} ${standardizedScore}`);
        formData.append('workExp', workExp);
        formData.append('budget', `£${budget}`);
        formData.append('scholarship', scholarship);
        formData.append('intake', intake);

        if (result) {
            formData.append('ai_match', result.topMatch.universityName);
            formData.append('ai_probability', result.topMatch.matchPercentage.toString());
        }

        try {
            const response = await fetch('/api/eligibility', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (step === 'result' && result) {
        if (submitted) {
            return (
                <div className="animate-fadeIn max-w-lg mx-auto text-center py-12">
                    <div className="w-20 h-20 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check size={40} />
                    </div>
                    <h3 className="text-2xl font-serif text-[#011E26] mb-2">Success! Enquiry Sent</h3>
                    <p className="text-slate-600">
                        Our admissions team has received your profile and the AI assessment. We will be in touch shortly to help you secure your place at {result.topMatch.universityName}.
                    </p>
                    <button
                        onClick={() => {
                            setStep('input');
                            setFormStep(1);
                            setSubmitted(false);
                            setShowSubmitForm(false);
                            setResult(null);
                        }}
                        className="mt-8 text-[#009B8F] font-bold uppercase tracking-widest text-xs hover:underline"
                    >
                        Start New Check
                    </button>
                </div>
            );
        }

        return (
            <div className="animate-fadeIn max-w-lg mx-auto">
                <div className="text-center mb-8">
                    <h3 className="text-2xl font-serif text-[#011E26]">AI Academic Match</h3>
                    <p className="text-[#009B8F] font-bold text-xs uppercase tracking-widest mt-1">Intelligence Matcher Results</p>
                </div>

                <div className="bg-[#011E26] text-white p-8 rounded-xl shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#009B8F] rounded-full filter blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity"></div>

                    <div className="flex items-center gap-6 mb-8">
                        <div className="relative w-24 h-24 flex-shrink-0">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-slate-700" />
                                <circle cx="48" cy="48" r="40" stroke="#009B8F" strokeWidth="6" fill="transparent" strokeDasharray={`${result.topMatch.matchPercentage * 2.51} 251`} className="transition-all duration-1000 ease-out" />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">{result.topMatch.matchPercentage}%</span>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold font-serif">{result.topMatch.universityName}</h2>
                            <p className="text-slate-400 text-sm">Match Probability</p>
                        </div>
                    </div>

                    <div className="space-y-3 mb-8 border-t border-slate-700/50 pt-6 text-sm">
                        <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Match Insights</p>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#009B8F]" />
                            <span>Academic: <span className="text-white">{result.topMatch.breakdown.academic}</span></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#009B8F]" />
                            <span>English: <span className="text-white">{result.topMatch.breakdown.english}</span></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#009B8F]" />
                            <span>Outcome: <span className="text-white">{result.topMatch.breakdown.budget}</span></span>
                        </div>
                    </div>

                    {!showSubmitForm ? (
                        <div className="grid grid-cols-1 gap-3">
                            <button
                                onClick={() => setShowSubmitForm(true)}
                                className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#009B8F]/20"
                            >
                                Get in touch with an Advisor <ArrowRight size={16} />
                            </button>
                            <button
                                onClick={() => navigate('/signup')}
                                className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm uppercase tracking-widest rounded border border-white/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Lock size={16} /> Sign Up to Unlock Roadmap
                            </button>
                        </div>
                    ) : (
                        <AdvisorContactForm 
                            serviceType="admissions"
                            onCancel={() => setShowSubmitForm(false)}
                            initialData={{
                                ai_match: result.topMatch.universityName,
                                ai_probability: result.topMatch.matchPercentage.toString()
                            }}
                        />
                    )}
                </div>

                {!showSubmitForm && (
                    <div className="mt-8 text-center">
                        <p className="text-slate-400 text-[10px] uppercase tracking-widest mb-4">Secured by Nex AI Protection</p>
                        <div className="grid grid-cols-1 gap-4">
                            {result.otherMatches.map((match, idx) => (
                                <div key={idx} className="bg-white p-4 rounded-lg shadow border border-slate-100 flex justify-between items-center group cursor-pointer hover:border-[#009B8F] transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full border-2 border-[#009B8F] flex items-center justify-center text-[#009B8F] font-bold text-sm">{match.matchPercentage}%</div>
                                        <span className="font-bold text-[#011E26]">{match.universityName}</span>
                                    </div>
                                    <button className="text-xs font-bold text-slate-400 uppercase tracking-wider group-hover:text-[#009B8F]">Details</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Progress Bar */}
            <div className="mb-10">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-serif text-[#011E26]">Admissions Support Form</h3>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Step {formStep} of 4</span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#009B8F] transition-all duration-500" style={{ width: `${(formStep / 4) * 100}%` }}></div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* --- STEP 1: DESTINATION & AMBITION --- */}
                {formStep === 1 && (
                    <div className="space-y-8 animate-fadeIn">
                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-4 flex items-center gap-2">
                                <MapPin size={14} /> 1. Select Destination(s)
                            </label>
                            <div className="space-y-6">
                                {DESTINATIONS.map(region => (
                                    <div key={region.region}>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-3">{region.region}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {region.countries.map(country => (
                                                <button
                                                    key={country}
                                                    type="button"
                                                    onClick={() => handleDestinationToggle(country)}
                                                    className={`px-4 py-2 rounded border text-xs transition-all ${destination === country
                                                        ? 'bg-[#009B8F] text-white border-[#009B8F] shadow-lg shadow-[#009B8F]/20'
                                                        : 'bg-white text-slate-600 border-gray-200 hover:border-[#009B8F]'
                                                        }`}
                                                >
                                                    {country}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {destination && (
                            <div className="animate-fadeIn">
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">🌍 Preferred City</label>
                                <select
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors text-sm"
                                >
                                    {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2 flex items-center gap-2">
                                <Globe size={14} /> 2. Target Degree Level
                            </label>
                            <select
                                value={degreeLevel}
                                onChange={(e) => setDegreeLevel(e.target.value)}
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm"
                            >
                                {DEGREE_LEVELS.map(cat => (
                                    <optgroup key={cat.category} label={cat.category}>
                                        {cat.levels.map(level => <option key={level} value={level}>{level}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2 flex items-center gap-2">
                                <Sparkles size={14} /> 3. Target Course / Major
                            </label>
                            <input
                                type="text"
                                value={course}
                                onChange={(e) => setCourse(e.target.value)}
                                placeholder="e.g. Computer Science, MBA, etc."
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm"
                            />
                            {/* Simple Auto-suggestion list */}
                            <div className="flex flex-wrap gap-2 mt-3">
                                {COURSE_CATEGORIES.filter(c => ["Business & Finance", "IT & Computer Science", "Engineering", "Health & Medicine", "Law & Legal"].includes(c.category)).map(cat => (
                                    <button
                                        key={cat.category}
                                        type="button"
                                        onClick={() => setCourse(cat.category)}
                                        className="text-[10px] bg-slate-100 text-slate-500 px-2 py-1 rounded hover:bg-[#009B8F]/10 hover:text-[#009B8F] transition-colors"
                                    >
                                        + {cat.category}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="button"
                                disabled={!destination || !course}
                                onClick={nextStep}
                                className="w-full py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                Next: Academic DNA <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* --- STEP 2: ACADEMIC DNA --- */}
                {formStep === 2 && (
                    <div className="space-y-8 animate-fadeIn">
                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Qualification</label>
                            <select value={qualification} onChange={(e) => setQualification(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm">
                                {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2 flex items-center justify-between">
                                <span>Academic Grade (AI Optimized)</span>
                                <Sparkles className="text-[#009B8F]" size={14} />
                            </label>
                            <input
                                type="text"
                                required
                                value={grade}
                                onChange={(e) => setGrade(e.target.value)}
                                placeholder="e.g. 3.5 GPA, 85%, or A Levels"
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Graduation Year</label>
                                <select value={gradYear} onChange={(e) => setGradYear(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm">
                                    {['2026 (Expected)', '2025', '2024', '2023', '2022', '2021', '2020 or earlier'].map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Number of Backlogs</label>
                                <select value={backlogs} onChange={(e) => setBacklogs(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm">
                                    {['0', '1-2', '3-5', '5+'].map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button type="button" onClick={prevStep} className="flex-1 py-4 border border-gray-200 text-slate-400 font-bold text-sm uppercase tracking-widest rounded hover:border-slate-300 transition-all flex items-center justify-center gap-2">
                                <ArrowLeft size={16} /> Back
                            </button>
                            <button type="button" disabled={!grade} onClick={nextStep} className="flex-[2] py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                                Next: Testing & Experience <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* --- STEP 3: COMPETITIVENESS & TESTING --- */}
                {formStep === 3 && (
                    <div className="space-y-8 animate-fadeIn">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">English Test</label>
                                <select value={englishTest} onChange={(e) => setEnglishTest(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm">
                                    {ENGLISH_TESTS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Overall Score</label>
                                <input
                                    type="text"
                                    value={englishScore}
                                    onChange={(e) => setEnglishScore(e.target.value)}
                                    placeholder="e.g. 7.5"
                                    disabled={englishTest.includes('None') || englishTest.includes('MOI')}
                                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none disabled:bg-gray-100 text-sm"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Standardized Test</label>
                                <select value={standardedTest} onChange={(e) => setStandardedTest(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm">
                                    <option value="">None</option>
                                    {STANDARDIZED_TESTS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2">Score (Optional)</label>
                                <input
                                    type="text"
                                    value={standardizedScore}
                                    onChange={(e) => setStandardizedScore(e.target.value)}
                                    placeholder="e.g. 320"
                                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-2 flex items-center gap-2">
                                <Briefcase size={14} /> Relevant Work Experience
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {WORK_EXPERIENCE.map(exp => (
                                    <button
                                        key={exp}
                                        type="button"
                                        onClick={() => setWorkExp(exp)}
                                        className={`p-3 rounded border text-[10px] uppercase font-bold tracking-widest transition-all ${workExp === exp ? 'bg-[#009B8F] text-white border-[#009B8F]' : 'bg-gray-50 text-slate-500 border-gray-200 hover:border-[#009B8F]'}`}
                                    >
                                        {exp}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button type="button" onClick={prevStep} className="flex-1 py-4 border border-gray-200 text-slate-400 font-bold text-sm uppercase tracking-widest rounded hover:border-slate-300 transition-all flex items-center justify-center gap-2">
                                <ArrowLeft size={16} /> Back
                            </button>
                            <button type="button" onClick={nextStep} className="flex-[2] py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2">
                                Next: Financial Filters <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* --- STEP 4: FINANCIAL & SUCCESS FILTERS --- */}
                {formStep === 4 && (
                    <div className="space-y-8 animate-fadeIn">
                        <div>
                            <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-6 flex justify-between items-center">
                                <div className="flex items-center gap-2"><DollarSign size={14} /> Max Annual Budget (Tuition)</div>
                                <span className="bg-[#009B8F]/10 text-[#009B8F] px-4 py-1 rounded-full font-mono text-base">£{budget.toLocaleString()}</span>
                            </label>
                            <input
                                type="range"
                                min="5000"
                                max="100000"
                                step="1000"
                                value={budget}
                                onChange={(e) => setBudget(parseInt(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#009B8F]"
                            />
                            <div className="flex justify-between text-[10px] text-slate-400 font-bold mt-3">
                                <span>£5,000</span>
                                <span>ECONOMY</span>
                                <span>ELITE</span>
                                <span>£100,000+</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-3">Scholarship Need</label>
                                <div className="space-y-2">
                                    {SCHOLARSHIPS.map(s => (
                                        <label key={s} className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-all ${scholarship === s ? 'border-[#009B8F] bg-[#009B8F]/5 text-[#009B8F]' : 'border-gray-200 text-slate-500'}`}>
                                            <input type="radio" value={s} checked={scholarship === s} onChange={() => setScholarship(s)} className="accent-[#009B8F]" />
                                            <span className="text-xs font-medium">{s}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#009B8F] uppercase tracking-wider block mb-3">Preferred Intake</label>
                                <div className="space-y-2">
                                    {INTAKES.map(i => (
                                        <label key={i} className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-all ${intake === i ? 'border-[#009B8F] bg-[#009B8F]/5 text-[#009B8F]' : 'border-gray-200 text-slate-500'}`}>
                                            <input type="radio" value={i} checked={intake === i} onChange={() => setIntake(i)} className="accent-[#009B8F]" />
                                            <span className="text-xs font-medium">{i}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button type="button" onClick={prevStep} className="flex-1 py-4 border border-gray-200 text-slate-400 font-bold text-sm uppercase tracking-widest rounded hover:border-slate-300 transition-all flex items-center justify-center gap-2">
                                <ArrowLeft size={16} /> Back
                            </button>
                            <button type="submit" className="flex-[2] py-4 bg-[#009B8F] hover:bg-[#00857a] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 shadow-xl shadow-[#009B8F]/20">
                                <Sparkles size={16} /> Run Global Intelligence Scan
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default AdmissionsForm;
